import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, TriangleAlert } from "lucide-react";
import type { CSSProperties } from "react";
import {
  EVENT_PLATFORM_META,
  EVENT_PLATFORMS,
  detailAfterPlatformChange,
  linkMatchesPlatform,
  platformTakesLink,
  type EventPlatform,
} from "@shared/eventPlatform";
import EventPlatformIcon from "./EventPlatformIcon";

/**
 * The location block for an event: which platform, its attendee link, and what
 * the catalog says publicly.
 *
 * Shared by the event creator and the Admin CRM drawer, which previously each
 * carried their own copy of the same three fields.
 *
 * Two rules shape it:
 *
 *  1. The link is not the location. `location` is public catalog text — it renders
 *     on the events page and is fed into artwork prompts — so a real meeting link
 *     placed there is readable by anyone browsing the site. The link goes to the
 *     event's `joinUrl`, which is validated as HTTPS and released to attendees
 *     after they register.
 *  2. "Other" describes a place, not a service. It has no link to ask for, so it
 *     keeps a single free-text location field.
 *
 * The two forms style their inputs differently, so both pass their own tokens in
 * rather than this component imposing a third look.
 */
export default function EventPlatformField({
  platform,
  detail,
  joinUrl,
  onPlatformChange,
  onDetailChange,
  onJoinUrlChange,
  fieldStyle,
  labelStyle,
  idPrefix = "event-location",
}: {
  platform: EventPlatform | "";
  /** The public note beside a named platform, or the whole location for "Other". */
  detail: string;
  /** The attendee link, kept out of the public location. */
  joinUrl: string;
  onPlatformChange: (next: EventPlatform | "", carriedDetail: string) => void;
  onDetailChange: (next: string) => void;
  onJoinUrlChange: (next: string) => void;
  fieldStyle: CSSProperties;
  labelStyle: CSSProperties;
  idPrefix?: string;
}) {
  const meta = platformTakesLink(platform) ? EVENT_PLATFORM_META[platform] : null;
  const linkLooksWrong = Boolean(joinUrl.trim()) && !linkMatchesPlatform(platform, joinUrl);

  const itemStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: ".55rem",
    padding: ".5rem .6rem",
    borderRadius: ".5rem",
    color: "#E4EAF4",
    fontFamily: "'Inter', sans-serif",
    fontSize: "var(--chi-body-compact)",
    cursor: "pointer",
    outline: "none",
  };

  return (
    <div style={{ display: "grid", gap: ".55rem" }}>
      <div style={{ ...labelStyle, display: "grid", gap: ".42rem" }}>
        <span id={`${idPrefix}-platform-label`}>Location</span>
        {/*
          Controlled for its whole lifetime. Passing `undefined` while nothing is
          chosen makes Radix treat the field as uncontrolled and then warn when the
          first choice turns it controlled; an empty string is a selection of
          nothing and renders the placeholder.
        */}
        <SelectPrimitive.Root
          value={platform}
          onValueChange={next =>
            onPlatformChange(
              next as EventPlatform,
              detailAfterPlatformChange(platform, next as EventPlatform, detail),
            )
          }
        >
          <SelectPrimitive.Trigger
            id={`${idPrefix}-platform`}
            aria-labelledby={`${idPrefix}-platform-label`}
            style={{
              ...fieldStyle,
              display: "flex",
              alignItems: "center",
              gap: ".55rem",
              textAlign: "left",
              cursor: "pointer",
            }}
          >
            {platform ? <EventPlatformIcon platform={platform} /> : null}
            <SelectPrimitive.Value placeholder="Select a platform" />
            <SelectPrimitive.Icon style={{ marginLeft: "auto", display: "grid", placeItems: "center" }}>
              <ChevronDown size={16} aria-hidden="true" />
            </SelectPrimitive.Icon>
          </SelectPrimitive.Trigger>
          <SelectPrimitive.Portal>
            <SelectPrimitive.Content
              position="popper"
              sideOffset={6}
              style={{
                /*
                 * This menu is portalled to document.body. Event forms can sit in
                 * modal layers as high as z-index 3000, so a normal page-menu
                 * z-index leaves the options open but hidden behind the dialog.
                 */
                zIndex: 3100,
                minWidth: "var(--radix-select-trigger-width)",
                padding: ".35rem",
                border: "1px solid rgba(255,255,255,.13)",
                borderRadius: ".72rem",
                background: "#101827",
                boxShadow: "0 18px 40px rgba(2,6,20,.55)",
              }}
            >
              <SelectPrimitive.Viewport style={{ display: "grid", gap: ".12rem" }}>
                {EVENT_PLATFORMS.map(option => (
                  <SelectPrimitive.Item
                    key={option}
                    value={option}
                    style={itemStyle}
                    onPointerEnter={event => {
                      event.currentTarget.style.background = "rgba(125,234,255,.1)";
                    }}
                    onPointerLeave={event => {
                      event.currentTarget.style.background = "transparent";
                    }}
                  >
                    <EventPlatformIcon platform={option} />
                    <SelectPrimitive.ItemText>{option}</SelectPrimitive.ItemText>
                    <SelectPrimitive.ItemIndicator style={{ marginLeft: "auto", display: "grid", placeItems: "center", color: "#7DEAFF" }}>
                      <Check size={15} aria-hidden="true" />
                    </SelectPrimitive.ItemIndicator>
                  </SelectPrimitive.Item>
                ))}
              </SelectPrimitive.Viewport>
            </SelectPrimitive.Content>
          </SelectPrimitive.Portal>
        </SelectPrimitive.Root>
      </div>

      {meta && (
        <>
          <label style={labelStyle} htmlFor={`${idPrefix}-join-url`}>
            <span>{meta.linkLabel}</span>
            <input
              id={`${idPrefix}-join-url`}
              type="url"
              inputMode="url"
              value={joinUrl}
              onChange={event => onJoinUrlChange(event.target.value)}
              placeholder={meta.linkExample}
              style={fieldStyle}
            />
          </label>
          {linkLooksWrong && (
            <p
              role="status"
              style={{
                display: "flex",
                alignItems: "center",
                gap: ".35rem",
                margin: 0,
                color: "#FCD34D",
                fontFamily: "'Inter', sans-serif",
                fontSize: "var(--chi-body-compact)",
              }}
            >
              <TriangleAlert size={14} aria-hidden="true" />
              That does not look like a {meta.linkHost} address. Saving it anyway is fine if your
              organization routes {platform} through its own domain.
            </p>
          )}
          {/*
            Kept beside the link because the link is private. This is the line the
            public catalog shows, and every event in the catalog already uses it to
            say "(link sent after registration)".
          */}
          <label style={labelStyle} htmlFor={`${idPrefix}-public-note`}>
            <span>Shown publicly (optional)</span>
            <input
              id={`${idPrefix}-public-note`}
              value={detail}
              onChange={event => onDetailChange(event.target.value)}
              placeholder="(link sent after registration)"
              style={fieldStyle}
            />
          </label>
        </>
      )}

      {platform === "Other" && (
        <label style={labelStyle} htmlFor={`${idPrefix}-platform-detail`}>
          <span>Location or platform</span>
          <input
            id={`${idPrefix}-platform-detail`}
            required
            aria-required="true"
            value={detail}
            onChange={event => onDetailChange(event.target.value)}
            placeholder="Enter the venue, city and state, or another platform"
            style={fieldStyle}
          />
        </label>
      )}
    </div>
  );
}
